/**
 * @fileoverview Widget and table builder, as a modal (MOR-906 §7.8)
 *
 * Composing a widget or a table is something you do *to* a surface, not a
 * place you go, so both builders live behind one dialog. The Dashboard opens
 * it with both kinds offered and pins what you save; the Reports tab locks it
 * to tables and leaves what you save in the library, to be starred onto the
 * board separately.
 *
 * @component
 * @example
 * <BuilderDialog
 *   orgId={orgId}
 *   kind="widget"
 *   onKindChange={setKind}
 *   open={open}
 *   onOpenChange={setOpen}
 *   pending={answer}
 *   onPendingConsumed={clear}
 * />
 */

import { ChartColumn, Table2 } from "lucide-react";

import type { Id } from "@cvx/_generated/dataModel";

import { Button } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import type { AnalyticsAnswer } from "./AskAnalyticsBox";
import { PivotReportBuilder } from "./PivotReportBuilder";
import { WidgetBuilder } from "./WidgetBuilder";

/** Which builder the dialog is showing. */
export type BuilderKind = "table" | "widget";

export function BuilderDialog({
  orgId,
  kind,
  onKindChange,
  lockKind,
  pinOnSave = true,
  heading,
  description,
  open,
  onOpenChange,
  pending,
  onPendingConsumed,
}: {
  orgId: Id<"organizations">;
  kind: BuilderKind;
  onKindChange?: (kind: BuilderKind) => void;
  /** Hide the kind toggle, for surfaces that only build one of the two. */
  lockKind?: boolean;
  /** Whether saving also pins the result to the Dashboard board. */
  pinOnSave?: boolean;
  heading?: string;
  description?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A spec handed over from the AI ask box, adopted once then cleared. */
  pending?: AnalyticsAnswer | null;
  onPendingConsumed?: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-6xl flex-col gap-4 overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {heading ?? (kind === "table" ? "New table" : "New widget")}
          </DialogTitle>
          <DialogDescription>
            {description ??
              (kind === "table"
                ? "Generate tables and select Save to add it to your dashboard."
                : "Generate widgets and select Save to add it to your dashboard.")}
          </DialogDescription>
        </DialogHeader>

        {!lockKind && (
          <div className="flex w-fit items-center rounded-md border border-border">
            <Button
              variant={kind === "widget" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 rounded-r-none"
              onClick={() => onKindChange?.("widget")}
            >
              <ChartColumn className="size-4" />
              Widget
            </Button>
            <Button
              variant={kind === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 rounded-l-none"
              onClick={() => onKindChange?.("table")}
            >
              <Table2 className="size-4" />
              Table
            </Button>
          </div>
        )}

        <div className="min-w-0 flex-1 overflow-y-auto">
          {kind === "table" ? (
            <PivotReportBuilder
              orgId={orgId}
              pendingSpec={pending?.shape === "pivot" ? pending.spec : null}
              pendingTitle={pending?.shape === "pivot" ? pending.title : null}
              onPendingConsumed={onPendingConsumed}
              pinOnSave={pinOnSave}
            />
          ) : (
            <WidgetBuilder
              orgId={orgId}
              pendingSpec={pending?.shape === "widget" ? pending.spec : null}
              pendingTitle={pending?.shape === "widget" ? pending.title : null}
              onPendingConsumed={onPendingConsumed}
              pinOnSave={pinOnSave}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
